import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Button,
  Breadcrumbs,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  FolderRounded,
  InsertDriveFileRounded,
  MoreVertRounded,
  DriveFileRenameOutlineRounded,
  DeleteRounded,
  FolderOpenRounded,
  DownloadRounded,
  HomeRounded,
  ArrowBackRounded,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { useDialog } from "../../DialogContext";
import { backend_api, backend_url } from "../../../static/server";
import { useAccess } from "../../Auth/AccessProvider";
import CreateFolderForm from "../Document/CreateFolderForm";
import UploadFileForm from "../Document/UploadFileForm";
import { formattedDate } from "../../../styles/styles";
import { DataGridNoData } from "../../../static/staticdata";
import DataTable from "../../DataTable";
import NoDocuments from "../../../assets/no-document.webp";
import axios from "axios";

const DocumentTableForDash = ({
  // Required props
  documents = [],
  loading = false,
  currentFolderData = null,

  // Optional props with defaults
  height = "100%",
  minHeight = 0,
  emptyStateText = "No documents found",
  emptyStateImage = NoDocuments,

  // Pagination props
  pagination = null,
  onPageChange = () => {},

  // Column visibility control
  hiddenColumns = [],
  showColumns = null,

  // Customization
  showActionColumn = true,
  showFilterChips = false,
  filterChips = [],
  showToolbar = true,
  CustomToolbar = null,
  extraBtn = null,
  customRowActions = null,
  enableOpenAction = true,
  enableEditAction = true,
  enableDeleteAction = true,

  // Callbacks
  onDocumentDelete = null,
  onDocumentEdit = null,
  onRefresh = () => {},
  onFolderNavigate = null,

  // External dependencies
  token = "",
  teamMemberLawyerId = "",

  // Current folder info
  currentFolderId = "",
  folders = [], // Array of {id, name} for breadcrumbs

  // Styles
  sx = {},
  tableProps = {},
}) => {
  const navigate = useNavigate();
  const { showDialog } = useDialog();
  const { hasAccess } = useAccess();

  // State management
  const [actionMenu, setActionMenu] = useState({
    anchorEl: null,
    rowData: null,
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteLoad, setDeleteLoad] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteStatus, setDeleteStatus] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Document operations
  const handleEditDocument = (data) => {
    if (onDocumentEdit) {
      onDocumentEdit(data);
    } else {
      if (data.type === "folder") {
        showDialog(
          <CreateFolderForm
            isEditMode={true}
            editfolderData={data.full_data}
          />,
          {
            dialogTitle: `Edit Folder - ${data.name || ""}`,
            onSuccess: onRefresh,
          }
        );
      } else {
        showDialog(
          <UploadFileForm fileData={data.full_data} isEditMode={true} />,
          {
            dialogTitle: `Edit File - ${data.name || ""}`,
            onSuccess: onRefresh,
          }
        );
      }
    }
  };

  const handleDeleteDocument = async (document) => {
    if (onDocumentDelete) {
      onDocumentDelete(document);
      return;
    }

    try {
      if (document.type === "folder") {
        // Delete folder API call
        const res = await axios.delete(
          `${backend_api}documents/document/folder/${document.id}/`,
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (res?.status === 204) {
          onRefresh();
          toast.success("Folder deleted successfully");
        }
      } else {
        // Delete file API call
        const res = await axios.delete(
          `${backend_api}documents/document/file/${document.id}/`,
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (res?.status === 204) {
          onRefresh();
          toast.success("File deleted successfully");
        }
      }
    } catch (error) {
      toast.error("Failed to delete. Please try again");
    }
  };

  const handleOpenFolder = (row) => {
    if (onFolderNavigate) {
      onFolderNavigate(row);
    } else {
      // Default navigation
      if (row.type === "folder") {
        const encodedName = encodeURIComponent(row.name || "");
        const currentPath = folders
          .map((f) => `${f.id}-${encodeURIComponent(f.name)}`)
          .join("/");
        const newSegment = `${row.id}-${encodedName}`;
        const newPath = currentPath
          ? `${currentPath}/${newSegment}`
          : newSegment;

        navigate(`/dashboard/documents/folder/${newPath}`);
      } else {
        // Open file in new tab
        if (row.file_url) {
          const fullUrl = row.file_url.startsWith("http")
            ? row.file_url
            : `${backend_url}${row.file_url}`;
          window.open(fullUrl, "_blank");
        } else {
          showSnackbar(
            `Cannot open file: ${row.name}. File URL not available.`,
            "error"
          );
        }
      }
    }
  };

  const handleDownload = (row) => {
    if (row.type === "file" && row.file_url) {
      const fullUrl = row.file_url.startsWith("http")
        ? row.file_url
        : `${backend_url}${row.file_url}`;

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = fullUrl;
      link.download = row.name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSnackbar(`Downloading: ${row.name}`, "success");
    }
  };

  const handleActionMenuOpen = (event, row) => {
    setActionMenu({
      anchorEl: event.currentTarget,
      rowData: row,
    });
  };

  const handleActionMenuClose = () => {
    setActionMenu({ anchorEl: null, rowData: null });
  };

  const handleOpenDeleteDialog = (row) => {
    setDeleteRow(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteRow(null);
    setDeleteProgress(0);
    setDeleteStatus("");
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;

    setDeleteLoad(true);
    try {
      await handleDeleteDocument(deleteRow);
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Delete error:", error);
      showSnackbar(`Failed to delete ${deleteRow.type}`, "error");
    } finally {
      setDeleteLoad(false);
    }
  };

  const showSnackbar = useCallback((message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  const handleBreadcrumbClick = useCallback(
    (index) => {
      if (index === -1) {
        navigate("/dashboard/documents");
      } else {
        const targetPath = folders
          .slice(0, index + 1)
          .map((f) => `${f.id}-${encodeURIComponent(f.name)}`)
          .join("/");
        navigate(`/dashboard/documents/folder/${targetPath}`);
      }
    },
    [folders, navigate]
  );

  const handleBack = useCallback(() => {
    if (folders.length > 1) {
      const parentPath = folders
        .slice(0, -1)
        .map((f) => `${f.id}-${encodeURIComponent(f.name)}`)
        .join("/");
      navigate(`/dashboard/documents/folder/${parentPath}`);
    } else {
      navigate("/dashboard/documents");
    }
  }, [folders, navigate]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0 || !bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Transform documents data to rows
  const rows = useMemo(() => {
    if (!Array.isArray(documents)) {
      console.error("documents is not an array:", documents);
      return [];
    }

    return documents.map((item) => {
      // Check if it's a folder or file based on available properties
      const isFolder =
        item?.folder_name !== undefined || item?.type === "folder";

      if (isFolder) {
        return {
          id: item?.id || `folder-${item?.originalId}`,
          name: item?.folder_name || item?.name || "--",
          type: "folder",
          category: item?.category?.category_name || "-",
          client: item?.case?.case_related_person
            ? `${item.case.case_related_person.first_name || ""} ${
                item.case.case_related_person.last_name || ""
              }`.trim()
            : item?.client || "-",
          matter: item?.case?.case_name || "-",
          fileCount: `${
            Array.isArray(item?.child_folder) ? item.child_folder.length : 0
          } folders, ${
            Array.isArray(item?.documents) ? item.documents.length : 0
          } files`,
          created_at: item?.created_at ? formattedDate(item.created_at) : "-",
          modified: item?.created_at ? formattedDate(item.created_at) : "-",
          size: "--",
          file_url: null,
          full_data: item,
        };
      } else {
        // It's a file
        const fileSize = item?.file_size_bytes
          ? formatFileSize(item.file_size_bytes)
          : item?.file_size || "--";

        return {
          id: item?.id || `file-${item?.originalId}`,
          name: item?.file_name || item?.name || "--",
          type: "file",
          size: fileSize,
          modified: item?.created_at ? formattedDate(item.created_at) : "-",
          created_at: item?.created_at ? formattedDate(item.created_at) : "-",
          file_url: item?.file || item?.file_url,
          category: item?.category?.category_name || "-",
          client: item?.case?.case_related_person
            ? `${item.case.case_related_person.first_name || ""} ${
                item.case.case_related_person.last_name || ""
              }`.trim()
            : "-",
          matter: item?.case?.case_name || "-",
          full_data: item,
        };
      }
    });
  }, [documents]);

  // All possible columns definition
  const allColumns = useMemo(
    () => [
      // Action Column
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => handleActionMenuOpen(e, params.row)}
              >
                <MoreVertRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={actionMenu.anchorEl}
              open={Boolean(actionMenu.anchorEl)}
              onClose={handleActionMenuClose}
              slotProps={{
                root: { sx: { ".MuiList-root": { padding: 0 } } },
              }}
            >
              {enableOpenAction && actionMenu.rowData && (
                <MenuItem
                  onClick={() => {
                    if (actionMenu.rowData.type === "folder") {
                      handleOpenFolder(actionMenu.rowData);
                    } else {
                      handleDownload(actionMenu.rowData);
                    }
                    handleActionMenuClose();
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  {actionMenu.rowData.type === "folder" ? (
                    <FolderOpenRounded
                      sx={{
                        fontSize: 18,
                        color: "action.active",
                      }}
                    />
                  ) : (
                    <DownloadRounded
                      sx={{
                        fontSize: 18,
                        color: "action.active",
                      }}
                    />
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {actionMenu.rowData.type === "folder"
                        ? "Open"
                        : "Download"}
                    </Typography>
                  </Box>
                </MenuItem>
              )}

              {enableEditAction &&
                actionMenu.rowData &&
                hasAccess("access_documents_edit") && (
                <MenuItem
                  id="access_documents_edit"
                  onClick={() => {
                    handleEditDocument(actionMenu.rowData);
                    handleActionMenuClose();
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <DriveFileRenameOutlineRounded
                    sx={{
                      fontSize: 18,
                      color: "action.active",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Edit
                    </Typography>
                  </Box>
                </MenuItem>
              )}
              {customRowActions &&
                customRowActions(actionMenu.rowData, {
                  closeMenu: handleActionMenuClose,
                })}

              {enableDeleteAction &&
                actionMenu.rowData &&
                hasAccess("access_documents_delete") && (
                <MenuItem
                  id="access_documents_delete"
                  onClick={() => {
                    handleOpenDeleteDialog(actionMenu.rowData);
                    handleActionMenuClose();
                  }}
                  sx={{
                    borderTop: "1px solid #e5e5e5",
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    color: "error.main",
                    "&:hover": {
                      bgcolor: "error.main",
                      color: "error.contrastText",
                      "& .MuiSvgIcon-root": {
                        color: "error.contrastText",
                      },
                    },
                  }}
                >
                  <DeleteRounded
                    sx={{
                      fontSize: 18,
                      color: "error.main",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Delete
                    </Typography>
                  </Box>
                </MenuItem>
              )}
            </Menu>
          </Box>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        flex: 2,
        minWidth: 250,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minHeight: "2rem",
              overflow: "hidden",
            }}
          >
            {params.row.type === "folder" ? (
              <FolderRounded
                sx={{
                  fontSize: "1rem",
                  color: "primary.main",
                  flexShrink: 0,
                }}
              />
            ) : (
              <InsertDriveFileRounded
                sx={{
                  fontSize: "1rem",
                  color: "text.secondary",
                  flexShrink: 0,
                }}
              />
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                lineHeight: 1,
                overflow: "hidden",
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  "&:hover": {
                    color:
                      params.row.type === "folder"
                        ? "primary.main"
                        : "secondary.main",
                  },
                }}
                onClick={() => {
                  if (params.row.type === "folder") {
                    handleOpenFolder(params.row);
                  } else {
                    handleDownload(params.row);
                  }
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {params.row.name}
                </Typography>
              </Box>

              {params.row.type === "folder" && params.row.fileCount && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.65rem",
                    color: "text.secondary",
                    lineHeight: 1,
                  }}
                >
                  {params.row.fileCount}
                </Typography>
              )}
            </Box>
          </Box>
        ),
      },
      {
        field: "type",
        headerName: "Type",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.row.type === "folder" ? "Folder" : "File"}
            size="small"
            color={params.row.type === "folder" ? "primary" : "default"}
            variant="outlined"
          />
        ),
      },
      {
        field: "size",
        headerName: "Size",
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.size}
          </Typography>
        ),
      },
      {
        field: "category",
        headerName: "Category",
        width: 150,
        renderCell: (params) => (
          <Chip
            label={params.row.category}
            size="small"
            sx={{ backgroundColor: "#E3F2FD" }}
          />
        ),
      },
      {
        field: "client",
        headerName: "Contact",
        width: 180,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
          >
            {params.row.client}
          </Typography>
        ),
      },
      {
        field: "matter",
        headerName: "Matter",
        width: 200,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              textOverflow: "ellipsis",
              overflow: "hidden",
              fontWeight: 500,
            }}
          >
            {params.row.matter}
          </Typography>
        ),
      },
      {
        field: "modified",
        headerName: "Modified",
        width: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.85rem" }}
          >
            {params.row.modified}
          </Typography>
        ),
      },
      {
        field: "created_at",
        headerName: "Created",
        width: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.85rem" }}
          >
            {params.row.created_at}
          </Typography>
        ),
      },
    ],
    [
      actionMenu,
      handleActionMenuOpen,
      handleActionMenuClose,
      handleOpenFolder,
      handleDownload,
      handleEditDocument,
      handleOpenDeleteDialog,
      hasAccess,
      customRowActions,
      enableOpenAction,
      enableEditAction,
      enableDeleteAction,
    ]
  );

  // Filter columns based on props
  const filteredColumns = useMemo(() => {
    let columns = allColumns;

    // If showColumns is provided, only include those columns
    if (showColumns && Array.isArray(showColumns)) {
      columns = columns.filter((col) => showColumns.includes(col.field));
    }

    // Remove hidden columns
    if (hiddenColumns && Array.isArray(hiddenColumns)) {
      columns = columns.filter((col) => !hiddenColumns.includes(col.field));
    }

    // Remove action column if disabled
    if (!showActionColumn) {
      columns = columns.filter((col) => col.field !== "actions");
    }

    return columns;
  }, [allColumns, showColumns, hiddenColumns, showActionColumn]);

  // Custom no rows overlay
  const CustomNoRowsOverlay = useCallback(
    () => <DataGridNoData imgSrc={emptyStateImage} text={emptyStateText} />,
    [emptyStateImage, emptyStateText]
  );

  // Breadcrumbs component
  const BreadcrumbsComponent = useCallback(
    () => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
        {folders.length > 0 && (
          <>
            <Tooltip title="Go back">
              <IconButton onClick={handleBack} size="small">
                <ArrowBackRounded sx={{ fontSize: "0.9rem" }} />
              </IconButton>
            </Tooltip>

            <Breadcrumbs
              separator="•"
              aria-label="breadcrumb"
              sx={{
                "& .MuiBreadcrumbs-separator": { mx: 1 },
              }}
            >
              <Typography
                component="span"
                onClick={() => handleBreadcrumbClick(-1)}
                sx={{
                  cursor: "pointer",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  "&:hover": { color: "primary.main" },
                  fontSize: "0.875rem",
                }}
              >
                <HomeRounded fontSize="small" />
                Documents
              </Typography>

              {folders.map((folder, index) => (
                <Typography
                  key={folder.id}
                  component="span"
                  onClick={() => handleBreadcrumbClick(index)}
                  sx={{
                    cursor: "pointer",
                    color:
                      index === folders.length - 1
                        ? "text.primary"
                        : "text.secondary",
                    fontWeight: index === folders.length - 1 ? 600 : 400,
                    "&:hover": { color: "primary.main" },
                    fontSize: "0.875rem",
                  }}
                >
                  {folder.name}
                </Typography>
              ))}
            </Breadcrumbs>
          </>
        )}
      </Box>
    ),
    [folders, handleBack, handleBreadcrumbClick]
  );

  const resolvedRowCount =
    pagination?.rowCount ?? pagination?.count ?? rows.length;

  return (
    <Box
      sx={{
        width: "100%",
        height,
        minHeight,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <DataTable
        columns={filteredColumns}
        rows={rows}
        extraBtn={extraBtn}
        CustomNoRowsOverlay={CustomNoRowsOverlay}
        CustomToolbar={CustomToolbar || BreadcrumbsComponent}
        height="100%"
        minHeight={0}
        loading={loading}
        page={pagination?.page || 1}
        setPage={(page) => onPageChange(page)}
        pageSize={pagination?.pageSize || 10}
        rowCount={resolvedRowCount}
        count={resolvedRowCount}
        hasNextPage={pagination?.hasNextPage || false}
        hasPreviousPage={pagination?.hasPreviousPage || false}
        filterChips={showFilterChips ? filterChips : []}
        showToolbar={showToolbar}
        {...tableProps}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Delete {deleteRow?.type === "folder" ? "Folder" : "File"}{" "}
            Confirmation
          </Typography>
          {deleteRow && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Are you sure you want to delete this {deleteRow.type}?
              {deleteRow.name && ` "${deleteRow.name}"`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {deleteRow?.type === "folder" && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                This action will permanently delete:
              </Typography>
              <Box sx={{ mt: 1, ml: 2 }}>
                <Typography variant="body2" color="error">
                  • The folder "{deleteRow?.name}"
                </Typography>
                <Typography variant="body2" color="error">
                  • All files inside this folder
                </Typography>
                <Typography variant="body2" color="error">
                  • All subfolders and their contents
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, fontWeight: "bold" }}
              >
                This action cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCloseDeleteDialog}
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={deleteLoad}
            size="small"
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ textTransform: "capitalize" }}
          >
            Delete {deleteRow?.type === "folder" ? "Folder" : "File"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default React.memo(DocumentTableForDash);
